/**
 * Platform observability via Firebase Analytics (GA4) and Performance Monitoring.
 * Crashlytics for web is not GA yet — use logPlatformError for non-fatal / global errors.
 */

import {
  getAnalytics,
  isSupported,
  logEvent,
  setUserId,
  setUserProperties,
  type Analytics,
} from 'firebase/analytics';
import { getClientConfig } from '../services/clientConfigService';
import { getEnvironment } from './environmentDetection';

let analyticsInstance: Analytics | null = null;
let analyticsInitStarted = false;

function isObservabilityEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  if (process.env.REACT_APP_DISABLE_OBSERVABILITY === '1') return false;
  if (process.env.NODE_ENV === 'development') return false;
  return true;
}

function sanitizeParam(value: string, maxLen = 100): string {
  return value.replace(/\s+/g, ' ').trim().slice(0, maxLen);
}

export function platformFailureReason(err: unknown, fallback = 'unknown'): string {
  if (err instanceof Error && err.message.trim()) {
    return sanitizeParam(err.message);
  }
  if (err && typeof err === 'object' && 'message' in err) {
    const msg = String((err as { message?: string }).message || '').trim();
    if (msg) return sanitizeParam(msg);
  }
  const raw = String(err ?? '').trim();
  return raw ? sanitizeParam(raw) : fallback;
}

/** Lazily initialize Analytics once; safe to call from any module. */
export async function ensurePlatformAnalytics(): Promise<Analytics | null> {
  if (!isObservabilityEnabled()) return null;
  if (analyticsInstance) return analyticsInstance;
  if (analyticsInitStarted) {
    return analyticsInstance;
  }
  analyticsInitStarted = true;
  try {
    const supported = await isSupported();
    if (!supported) return null;
    const { app } = await import('../firebase/firebaseConfig');
    analyticsInstance = getAnalytics(app);
    const config = getClientConfig();
    setUserProperties(analyticsInstance, {
      client_id: config.clientId ?? 'unknown',
      environment: getEnvironment(),
    });
    return analyticsInstance;
  } catch {
    return null;
  }
}

export function logPlatformEvent(eventName: string, params?: Record<string, string | number>): void {
  void ensurePlatformAnalytics().then((analytics) => {
    if (!analytics) return;
    try {
      logEvent(analytics, eventName, params);
    } catch {
      // Analytics blocked or unavailable.
    }
  });
}

export function logPlatformError(params: {
  surface: string;
  fatal?: boolean;
  reason?: string;
  componentStack?: string;
}): void {
  logPlatformEvent('platform_error', {
    surface: sanitizeParam(params.surface, 64),
    fatal: params.fatal ? 1 : 0,
    ...(params.reason ? { reason: sanitizeParam(params.reason, 200) } : {}),
    ...(params.componentStack
      ? { component_stack: sanitizeParam(params.componentStack.replace(/\s+/g, ' '), 300) }
      : {}),
  });
}

export function syncPlatformAnalyticsUser(params: {
  uid: string | null;
  roleId?: string | null;
  isCmcAdmin?: boolean;
  isModelPortalUser?: boolean;
}): void {
  void ensurePlatformAnalytics().then((analytics) => {
    if (!analytics) return;
    try {
      setUserId(analytics, params.uid);
      setUserProperties(analytics, {
        signed_in: params.uid ? 'yes' : 'no',
        ...(params.roleId ? { role_id: params.roleId } : {}),
        ...(params.isCmcAdmin != null ? { is_cmc_admin: params.isCmcAdmin ? 'yes' : 'no' } : {}),
        ...(params.isModelPortalUser != null
          ? { is_model_portal: params.isModelPortalUser ? 'yes' : 'no' }
          : {}),
      });
    } catch {
      // ignore
    }
  });
}

export function registerPlatformGlobalErrorHandlers(): void {
  if (!isObservabilityEnabled() || typeof window === 'undefined') return;

  window.addEventListener('error', (event) => {
    logPlatformError({
      surface: 'window_error',
      fatal: true,
      reason: platformFailureReason(event.error ?? event.message),
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    logPlatformError({
      surface: 'unhandled_rejection',
      fatal: false,
      reason: platformFailureReason(event.reason),
    });
  });
}

export { sanitizeParam as platformAnalyticsSanitizeParam };
